import { EventModel } from "@bryntum/scheduler";
import { Delete, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, Grid, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { HaulToFromForm, MemoForm, TimePicker } from "../../../Components/CommonInputs";
import { IEntityAutocomplete } from "../../../Components/CommonInputs/IEntityAutocomplete";
import { IFormProps, FormInput } from "../../../Components/CoreLib/library";
import { ConfirmDialog } from "../../../Components/Core/ConfirmDialog/ConfirmDialog";
import { Address, BrokerDto, DriverDto, EquipmentDto, OrderByType, OrderLineItemDto, RegionDto } from "../../../dtos";
import { AssignmentType } from "../../../dtos/generated/AssignmentType";
import { useGetBrokerQuery, useGetDriverQuery, useGetEquipmentQuery, useGetRegionQuery } from "../../../store/generated/generatedApi";
import { useEventEditorForm } from "./useEventEditorForm";
import { LineItemTypes } from "../../../util";

export interface IEventEditorProps extends IFormProps<EventModel> {
    handleDelete: () => void;
    open: boolean;
    isHaulerView: boolean;
    disabled: boolean;
    orderLineItemDto?: OrderLineItemDto;
    calculateRemainingHaulQuantity: (resourceId: string, excludeDispatchId?: string) => number | undefined;
}

export const EventEditor: FC<IEventEditorProps> = (props) => {
    const {
        handleDelete,
        open,
        isHaulerView,
        disabled,
        initValues,
        orderLineItemDto,
    } = props;
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        startTime,
        endTime,
        yardTime,
        assignmentType,
        driver,
        broker,
        equipment,
        handleFormDispatchBillingMemoChange,
        handleHaulToAddressChange,
        handleHaulFromAddressChange,
        setFormDriverComment,
        formDispatchBillingMemo,
        formDriverComment,
        formHaulToAddressLine1,
        formHaulToAddressLine2,
        formHaulToCity,
        formHaulToState,
        formHaulToZipCode,
        formHaulToCountry,
        formHaulToDirections,
        formHaulFromAddressLine1,
        formHaulFromAddressLine2,
        formHaulFromCity,
        formHaulFromState,
        formHaulFromZipCode,
        formHaulFromCountry,
        formHaulFromDirections,
        formHaulToRegion,
        formHaulFromRegion,
        formHaulToSiteName,
        formHaulFromSiteName,
        formHaulQuantity,
        setFormHaulToSiteName,
        setFormHaulFromSiteName,
        setFormHaulToRegion,
        setFormHaulFromRegion,
        setStartTime,
        setEndTime,
        setYardTime,
        setAssignmentType,
        setDriver,
        setBroker,
        setEquipment,
        setFormHaulQuantity,
    } = useEventEditorForm({ ...props, isHaulerView, orderLineItem: orderLineItemDto });

    const { data: brokers, isFetching: isFetchingBrokers } = useGetBrokerQuery({ searchText: '', sorting: ['CODE+'], page: 0, pageSize: 100000, includeInactive: false });
    const { data: drivers, isFetching: isFetchingDrivers } = useGetDriverQuery({ searchText: '', sorting: ['CODE+'], page: 0, pageSize: 100000, includeInactive: false });
    const { data: equipmentList, isFetching: isFetchingEquipmentList } = useGetEquipmentQuery({ searchText: '', sorting: ['NUMBER+'], page: 0, pageSize: 100000, includeInactive: false, brokerId: broker?.id });
    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    const event = useMemo(() => {
        const event = (initValues as any).data;
        return event;
    }, [initValues]);

    const hasHaulQuantity = useMemo(() => {
        return orderLineItemDto?.orderBy !== OrderByType.Equipment && !(orderLineItemDto?.lineItemType === LineItemTypes.Hourly && orderLineItemDto?.orderBy === OrderByType.Quantity);
    }, [orderLineItemDto]);

    return (
        <Dialog open={open} maxWidth='md' fullWidth>
            <DialogTitle sx={{ backgroundColor: 'primary', color: '#ffffff' }}>
                <Grid container direction='row' alignItems='center' gap={1}>
                    Edit Dispatch
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Grid container direction='column' mt={1} spacing={1}>
                    <Grid item xs={12} container direction='row' spacing={1}>
                        <Grid item xs={4}>
                            <TimePicker
                                label='Yard Time'
                                value={yardTime}
                                onChange={(date) => {
                                    if (date) {
                                        if (!isNaN(Date.parse(date.toString()))) {
                                            setYardTime(date);
                                        }
                                    }
                                }}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TimePicker
                                label='Onsite Time'
                                value={startTime}
                                onChange={(date) => {
                                    if (date) {
                                        if (!isNaN(Date.parse(date.toString()))) {
                                            setStartTime(date);
                                        }
                                    }
                                }}
                                error={!!fieldErrors.get('startTime')}
                                errorText={fieldErrors.get('startTime')}
                                required
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TimePicker
                                label='End Time'
                                value={endTime}
                                onChange={(date) => {
                                    if (date) {
                                        if (!isNaN(Date.parse(date.toString()))) {
                                            setEndTime(date);
                                        }
                                    }
                                }}
                                error={!!fieldErrors.get('endTime')}
                                errorText={fieldErrors.get('endTime')}
                                required
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth error={!!fieldErrors.get('assignmentType')} disabled={disabled} required>
                            <FormLabel>
                                Assignment Type
                            </FormLabel>
                            <Select
                                required
                                value={assignmentType?.toString() ?? ''}
                                disabled={disabled}
                                onChange={(event: SelectChangeEvent) => {
                                    const type = parseInt(event.target.value);
                                    setAssignmentType(type);
                                    switch (type) {
                                        case AssignmentType.Broker:
                                            setDriver(undefined);
                                            setEquipment(undefined);
                                            break;
                                        case AssignmentType.Driver:
                                            setBroker(undefined);
                                            setEquipment(undefined);
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                            >
                                <MenuItem value={AssignmentType.Broker.toString()}>
                                    Broker
                                </MenuItem>
                                <MenuItem value={AssignmentType.Driver.toString()}>
                                    Driver
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {assignmentType === AssignmentType.Broker ?
                        <Grid item container direction='column' xs={12} spacing={1}>
                            <Grid item xs={12}>
                                <FormControl fullWidth required error={!!fieldErrors.get('brokerId')}>
                                    <FormLabel>Broker</FormLabel>
                                    <IEntityAutocomplete
                                        options={brokers?.pageResults ?? []}
                                        onChange={(e, value) => {
                                            setBroker(value);
                                            setDriver(undefined);
                                            setEquipment(undefined);
                                        }}
                                        value={broker ?? null}
                                        getOptionLabel={(option: BrokerDto) => `${option.code} - ${option.name}`}
                                        error={!!fieldErrors.get('brokerId')}
                                        disabled={disabled}
                                        isLoading={isFetchingBrokers}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        :
                        <Grid item container direction='column' xs={12} spacing={1}>
                            <Grid item xs={12}>
                                <FormControl fullWidth required error={!!fieldErrors.get('driverId')}>
                                    <FormLabel>Driver</FormLabel>
                                    <IEntityAutocomplete
                                        options={drivers?.pageResults ?? []}
                                        onChange={(e, value?: DriverDto) => {
                                            setDriver(value);
                                            setEquipment(value?.equipment)
                                            setBroker(undefined);
                                        }}
                                        value={driver ?? null}
                                        getOptionLabel={(option: DriverDto) => `${option.code} - ${option.name}`}
                                        error={!!fieldErrors.get('driverId')}
                                        disabled={disabled}
                                        isLoading={isFetchingDrivers}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <FormControl fullWidth required={assignmentType === AssignmentType.Driver} error={!!fieldErrors.get('equipmentId')}>
                            <FormLabel>Equipment</FormLabel>
                            <IEntityAutocomplete
                                options={equipmentList?.pageResults ?? []}
                                onChange={(e, value) => { setEquipment(value); }}
                                value={equipment ?? null}
                                getOptionLabel={(option: EquipmentDto) => `${option.number} - ${option.equipmentType?.type}`}
                                error={!!fieldErrors.get('equipmentId')}
                                disabled={disabled}
                                isLoading={isFetchingEquipmentList}
                            />
                        </FormControl>
                    </Grid>
                    {hasHaulQuantity &&
                        <Grid item xs={4}>
                            <FormInput
                                value={(formHaulQuantity ?? 0).toString()}
                                label='Haul Quantity'
                                fullWidth
                                onChange={(event) => {
                                    const value = event.target.value;
                                    if (!isNaN(Number(value))) {
                                        setFormHaulQuantity(Number(value));
                                    } else {
                                        setFormHaulQuantity(0);
                                    }
                                }}
                                errorText={fieldErrors.get('haulQuantity')}
                                disabled={disabled}
                            />
                        </Grid>}
                    <Grid item container direction='column'>
                        <Grid item xs={12} sx={{ paddingTop: '8px' }}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    Haul To/From
                                </AccordionSummary>
                                <AccordionDetails>
                                    <HaulToFromForm
                                        disabled={disabled}
                                        haulToAddressLine1={formHaulToAddressLine1}
                                        haulToAddressLine2={formHaulToAddressLine2}
                                        haulToCity={formHaulToCity}
                                        haulToState={formHaulToState}
                                        haulToZipCode={formHaulToZipCode}
                                        haulToCountry={formHaulToCountry}
                                        haulToDirections={formHaulToDirections}
                                        haulFromAddressLine1={formHaulFromAddressLine1}
                                        haulFromAddressLine2={formHaulFromAddressLine2}
                                        haulFromCity={formHaulFromCity}
                                        haulFromState={formHaulFromState}
                                        haulFromZipCode={formHaulFromZipCode}
                                        haulFromCountry={formHaulFromCountry}
                                        haulFromDirections={formHaulFromDirections}
                                        haulToRegion={formHaulToRegion}
                                        haulFromRegion={formHaulFromRegion}
                                        haulToSiteName={formHaulToSiteName}
                                        haulFromSiteName={formHaulFromSiteName}
                                        enableLookup
                                        regions={regions?.pageResults ?? []}
                                        handleHaulFromSiteNameChange={(value) => {
                                            setFormHaulFromSiteName(value);
                                        }}
                                        handleHaulToSiteNameChange={(value) => {
                                            setFormHaulToSiteName(value);
                                        }}
                                        handleHaulToRegionChange={(value) => {
                                            setFormHaulToRegion(value);
                                        }}
                                        handleHaulFromRegionChange={(value) => {
                                            setFormHaulFromRegion(value);
                                        }}
                                        handleHaulFromAddressChange={(address: Address) => {
                                            handleHaulFromAddressChange(address);
                                        }}
                                        handleHaulToAddressChange={(address: Address) => {
                                            handleHaulToAddressChange(address);
                                        }}
                                        handleSwap={(haulFromAddress: Address, haulToAddress: Address) => {
                                            handleHaulToAddressChange(haulFromAddress);
                                            handleHaulFromAddressChange(haulToAddress);

                                            setFormHaulToRegion(haulFromAddress.region);
                                            setFormHaulFromRegion(haulToAddress.region);

                                            setFormHaulToSiteName(!!haulFromAddress.siteName ? haulFromAddress.siteName : '');
                                            setFormHaulFromSiteName(!!haulToAddress.siteName ? haulToAddress.siteName : '');
                                        }}
                                        handleHaulFromSiteLookup={(address: { addressLine1: string, addressLine2?: string, country: string, city: string, state: string, zipCode: string }, siteName?: string, region?: RegionDto) => {
                                            handleHaulFromAddressChange(address);
                                            setFormHaulFromRegion(region);
                                            setFormHaulFromSiteName(!!siteName ? siteName : '');
                                        }}
                                        handleHaulToSiteLookup={(address: { addressLine1: string, addressLine2?: string, country: string, city: string, state: string, zipCode: string }, siteName?: string, region?: RegionDto) => {
                                            handleHaulToAddressChange(address);
                                            setFormHaulToRegion(region);
                                            setFormHaulToSiteName(!!siteName ? siteName : '');
                                        }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant='h6'>Memos</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <MemoForm
                            hideQuoteMemo={true}
                            hideLineItemMemo={true}
                            quoteMemo={event?.quoteMemo}
                            dispatchBillingMemo={formDispatchBillingMemo}
                            lineItemMemo={event?.lineItemMemo}
                            orderLineItemDriverMemo={event?.driverMemo}
                            driverComment={formDriverComment}
                            handleDriverCommentChange={setFormDriverComment}
                            handleDispatchBillingMemoChange={handleFormDispatchBillingMemoChange}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' disabled={!isFormDirty || disabled} onClick={handleSave}>
                    SAVE
                </Button>
                <Button variant='contained' disabled={disabled} onClick={() => setShowDeleteConfirmationDialog(true)}>
                    DELETE
                </Button>
                <Button variant='outlined' onClick={handleCancel}>
                    CANCEL
                </Button>
            </DialogActions>
            <ConfirmDialog
                open={showDeleteConfirmationDialog}
                onClose={() => setShowDeleteConfirmationDialog(false)}
                content={<Typography>Are you sure you want to delete this dispatch? This action cannot be undone.</Typography>}
                title="Delete Dispatch"
                titleIcon={<Delete />}
                cancelText="Cancel"
                confirmText="Delete"
                onConfirm={() => {
                    handleDelete();
                    setShowDeleteConfirmationDialog(false);
                }}
                requireConfirmation={false}
            />
        </Dialog>
    );
}